import {
  AccountType,
  User,
  VehicleType,
  AttorneyWithUser,
  AssetType,
  BusinessType,
  IncomeType,
  Client,
  PayoutStatus,
  PurchaseItem,
  AttorneyStage,
  DocumentWithRelations,
  AttorneyReviewStatus,
} from "../../../api/src/types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import flags from "react-phone-number-input/flags";
import * as allFlags from "country-flag-icons/react/3x2";
import { DropDownOptionWithIcon } from "../components/forms/Dropdown";
import { z } from "zod";
import type { AttorneyReviewsGetResponse } from "../../../api/src/users/attorney/attorney-reviews/route";
import { Appointment } from "../pages/attorney/appointments/utils";
import { ServiceType } from "../pages/attorney-services/utils";
import { SingleAttorneyGetResponse } from "../../../api/src/users/attorney-services/route";
import {
  COLOR_PURPLE_TEXT,
  COLOR_PAID_TEXT,
  COLOR_UNDER_REVIEW_TEXT,
  COLOR_DUE_TEXT,
  COLOR_DEACTIVATED_TEXT,
  COLOR_GREEN_BACKGROUND,
  COLOR_ERROR_BACKGROUND,
} from "../components/theme";

export const formatUserFirstName = (user?: User | null) => {
  if (!user?.firstName) return user?.gender === "FEMALE" ? "Fiancée" : "Fiancé";

  return user.firstName;
};

/**
 * Helper function to extract initials from a name part
 * Handles multi-part names separated by spaces or hyphens
 */
const getInitialsFromNamePart = (
  namePart: string | null | undefined,
  splitPattern = /[-\s]+/,
): string => {
  return (
    namePart
      ?.split(splitPattern)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") ?? ""
  );
};

export const formatUserInitials = (user?: User | null) => {
  if (!user) return "";

  const firstNameInitials = getInitialsFromNamePart(user.firstName);
  const middleInitial = getInitialsFromNamePart(user.middleName);
  const lastNameInitials = getInitialsFromNamePart(user.lastName);

  return `${firstNameInitials}${middleInitial}${lastNameInitials}`;
};

export const formatUserFullName = (
  user?: Pick<User, "firstName" | "middleName" | "lastName" | "suffix"> | null,
  mode: "full" | "firstLast" = "full",
) => {
  if (!user) return "";
  let parts: (string | null)[] = [];
  switch (mode) {
    case "full":
      parts = [
        user.firstName,
        user.middleName,
        user.lastName,
        user.suffix,
      ].filter(Boolean);
      break;
    case "firstLast":
      parts = [user.firstName, user.lastName, user.suffix].filter(Boolean);
      break;
  }
  return parts.join(" ");
};

export const formatAccountType = (accountType: AccountType | null): string => {
  if (!accountType) return "Account";
  return {
    CHECKING_ACCOUNT: "Checking Account",
    SAVINGS_ACCOUNT: "Savings Account",
    HEALTH_SAVINGS_ACCOUNT: "Health Savings Account",
    CASH_ON_HAND: "Cash on Hand",
    CERTIFICATE_OF_DEPOSIT: "Certificate of Deposit",
    CREDIT_UNION_ACCOUNT: "Credit Union Account",
    FUNDS_HELD_IN_ESCROW: "Funds Held in Escrow",
    STOCKS: "Stocks",
    BONDS: "Bonds",
    BOND_FUND: "Bond Fund",
    NOTES_HELD: "Notes Held",
    BROKERAGE_ACCOUNT: "Brokerage Account",
    MANAGED_INVESTMENT_ACCOUNT: "Managed Investment Account",
    CASH_IN_BROKERAGE_ACCOUNT: "Cash in Brokerage Account",
    MONEY_MARKET_ACCOUNT: "Money Market Account",
    OTHER: "Other",
  }[accountType];
};

export const formatVehicleType = (vehicleType: VehicleType | null): string => {
  if (!vehicleType) return "Vehicle";
  return {
    CAR: "Car",
    TRUCK: "Truck",
    COMMERCIAL_VEHICLE: "Commercial Vehicle",
    RECREATIONAL: "Recreational Vehicle",
    MOTORCYCLE_SCOOTER_MOPED: "Motorcycle, Scooter, or Moped",
    BOAT: "Boat",
    AIRPLANE: "Airplane",
    OTHER: "Other",
  }[vehicleType];
};

export const formatAssetType = (assetType: AssetType | null): string => {
  if (!assetType) return "Asset";
  return {
    PLAN_401K: "401k",
    IRAs: "IRAs",
    MUTUAL_FUND: "Mutual Fund",
    THRIFT_SAVINGS_PLAN_TSP: "Thrift Savings Plan (TSP)",
    KEOGH: "Keogh",
    PROFIT_SHARING: "Profit Sharing",
    US_SAVINGS_BOND: "US Savings Bond",
    CRYPTOCURRENCY: "Cryptocurrency",
    DIGITAL_ASSET: "Digital Asset",
    VESTED_RESTRICTED_STOCK_UNITS: "Vested Restricted Stock Units",
    UNVESTED_RESTRICTED_STOCK_UNITS: "Unvested Restricted Stock Units",
    INCENTIVE_STOCK_OPTIONS: "Incentive Stock Options",
    DEFERRED_COMPENSATION: "Deferred Compensation",
    ANNUITY: "Annuity",
    LIFE_INSURANCE_CASH_VALUE: "Life Insurance Cash Value",
    INTELECTUAL_PROPERTY_INTEREST: "Intellectual Property Interest",
    JUDGEMENTS_LIENS: "Judgements & Liens",
    JEWELRY: "Jewelry",
    CREDIT_CARD_POINTS: "Credit Card Points",
    CONTENT_OF_SAFE_OR_SAFE_DEPOSIT_BOX: "Content of Safe or Safe Deposit Box",
    FIREARMS: "Firearms",
    COLLECTIONS: "Collections",
    TOOLS_EQUIPMENT: "Tools & Equipment",
    CROPS_LIVESTOCK: "Crops & Livestock",
    HOME_FURNISHINGS: "Home Furnishings",
    ARTS_AND_ANTIQUES: "Arts & Antiques",
    PENSION: "Pension",
    OTHER_RETIREMENT_PLANS: "Other Retirement Plans",
    OTHER: "Other",
  }[assetType];
};

export const formatBusinessType = (
  businessType: BusinessType | null,
): string => {
  if (!businessType) return "Business";
  return {
    CORPORATION: "Corporation",
    GENERAL_PARTNERSHIP: "General Partnership",
    LIMITED_LIABILITY_COMPANY: "Limited Liability Company",
    LIMITED_LIABILITY_PARTNERSHIP: "Limited Liability Partnership",
    LIMITED_PARTNERSHIP: "Limited Partnership",
    S_CORPORATION: "S Corporation",
    SOLE_PROPRIETORSHIP: "Sole Proprietorship",
  }[businessType];
};

export const formatIncomeType = (incomeType: IncomeType | null): string => {
  if (!incomeType) return "Income";
  return {
    BASE_SALARY: "Base Salary",
    BASE_WAGES: "Base Wages",
    SELF_EMPLOYMENT: "Self-Employment",
    SS_DISABILITY: "SS Disability",
    SS_UNEMPLOYMENT_INSURANCE: "SS Unemployment Insurance",
    SS_WORKERS_COMPENSATION: "SS Workers Compensation",
    PA_CHILD_SUPPORT: "PA Child Support",
    PA_ALIMONY_RECEIVED: "PA Alimony Received",
    RENTAL_INCOME: "Rental Income",
    ROYALTIES: "Royalties",
    CONTRIBUTIONS_FROM_HOUSEHOLD_MEMBERS:
      "Contributions from Household Members",
    OTHER: "Other",
  }[incomeType];
};

export const currencyOptions = {
  USD: { label: "US Dollar ($)", symbol: "$", icon: flags.US },
  AUD: { label: "Australian Dollar (A$)", symbol: "A$", icon: flags.AU },
  CAD: { label: "Canadian Dollar (C$)", symbol: "C$", icon: flags.CA },
  CNY: { label: "Chinese Yuan (¥)", symbol: "¥", icon: flags.CN },
  EUR: { label: "Euro (€)", symbol: "€", icon: allFlags.EU },
  INR: { label: "Indian Rupee (₹)", symbol: "₹", icon: flags.IN },
  JPY: { label: "Japanese Yen (¥)", symbol: "¥", icon: flags.JP },
  SGD: { label: "Singapore Dollar (S$)", symbol: "S$", icon: flags.SG },
  CHF: { label: "Swiss Franc (CHF)", symbol: "CHF", icon: flags.CH },
  GBP: { label: "British Pound (£)", symbol: "£", icon: flags.GB },
};

export type Currency = keyof typeof currencyOptions;

export const currencyDropdownOptions: DropDownOptionWithIcon[] = Object.entries(
  currencyOptions,
).map(([value, { label, icon }]) => ({
  label,
  value,
  Icon: icon as React.ComponentType,
}));

export const guardSupportedCurrency = (
  currency: string | null,
): currency is keyof typeof currencyOptions => {
  return currency !== null && Object.keys(currencyOptions).includes(currency);
};

export const formatCurrency = (
  valueInCents: bigint | number | null,
  currency: string | null,
) => {
  if (valueInCents === null) return "-";
  const value = Number(valueInCents) / 100;

  if (guardSupportedCurrency(currency)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  return `${value}`;
};

export const formatAttorneyName = (
  attorney?: AttorneyWithUser | null,
): string | null => {
  if (!attorney) return null;
  return `${attorney.user.firstName} ${attorney.user.lastName}`;
};

const zError = z.object({
  error: z.object({
    name: z.string(),
    issues: z.array(
      z.object({
        message: z.string(),
      }),
    ),
  }),
});

export const formatErrorString = (
  error: FetchBaseQueryError | SerializedError,
): string => {
  if ("message" in error) {
    return error.message;
  }
  if ("data" in error) {
    const zData = zError.safeParse(error.data);
    if (zData.success) {
      const zodIssues = zData.data.error.issues;
      return zodIssues.map((issue) => issue.message).join(". ");
    }
    if (
      typeof error.data === "object" &&
      error.data !== null &&
      "error" in error.data &&
      typeof error.data.error === "string"
    ) {
      return error.data.error;
    }
    return `${error.data}`;
  }
  return "An unknown error occurred. Please try refreshing the page.";
};

export const sanitizeCurrencyInput = (value: string): string => {
  const sanitized = value.replace(/[^0-9.-]/g, "");
  return removeLeadingZeros(sanitized);
};

/**
 * Removes leading zeros from a numeric input string.
 * Handles cases like:
 * - "0123" → "123"
 * - "00.123" → "0.123" (keeps zero before decimal)
 * - "0" → "0" (preserves single zero)
 * - "-0123" → "-123" (handles negative numbers)
 */
export const removeLeadingZeros = (value: string): string => {
  if (!value) return value;

  // Handle negative numbers separately
  if (value.startsWith("-")) {
    return "-" + removeLeadingZeros(value.substring(1));
  }

  // Handle decimal numbers (preserve zero before decimal point)
  if (value.includes(".")) {
    const parts = value.split(".");
    // Handle cases with multiple dots (take first as integer, rest join with decimal)
    const integer = parts[0] ?? "0"; // If integer part is empty, use "0"
    const decimal = parts.slice(1).join(".");
    // Remove leading zeros from integer part, but keep at least one digit
    const cleanedInteger = integer.replace(/^0+(?=\d)/, "");
    return `${cleanedInteger || "0"}.${decimal}`;
  }

  // For non-decimal numbers, remove all leading zeros except if the result would be empty
  const result = value.replace(/^0+(?=\d)/, "");
  return result || "0";
};

export const parseAndFormatCurrency = (
  rawValue: string,
  currency = "USD",
): string | null => {
  const parsed = parseFloat(rawValue);
  if (isNaN(parsed)) return null;
  return formatCurrency(parsed * 100, currency);
};

export const formatListOfStrings = (list: string[]): string => {
  return list.join(", ");
};

export const formatLongTextPreview = (
  text: string | null,
  maxLength = 200,
  showMore: boolean,
): string => {
  if (!text) return "";
  if (showMore) return text;
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export type BarRatingValue = 1 | 2 | 3 | 4 | 5;

export const coerceToBarRatingValue = (value: number): BarRatingValue => {
  return Math.min(Math.max(1, Math.floor(value / 10)), 5) as BarRatingValue;
};

export const formatAttorneyReviewRating = (
  attorneyReviews:
    | AttorneyReviewsGetResponse
    | SingleAttorneyGetResponse["attorneyReviews"],
): number => {
  let attorneyReviewsLength = attorneyReviews.length;
  const totalRating = attorneyReviews.reduce((acc: number, review) => {
    if (review.rating > 50 || review.rating < 0) {
      attorneyReviewsLength--;
      return acc;
    }
    return acc + review.rating;
  }, 0);

  // Return 0 if no valid reviews to avoid NaN
  if (attorneyReviewsLength === 0) {
    return 0;
  }

  const averageRating = Number(
    (totalRating / attorneyReviewsLength / 10).toFixed(1),
  );
  return averageRating;
};

export const getAppointmentsParagraphData = (appointmentsLength: number) => {
  return {
    count: appointmentsLength,
    appointmentText: appointmentsLength === 1 ? "appointment" : "appointments",
  };
};

export const formatUserPronouns = (user: User | Client) => {
  if (user.gender === "MALE") {
    return "he/him/his";
  } else if (user.gender === "FEMALE") {
    return "she/her/hers";
  } else if (
    user.gender === "NON_BINARY" ||
    user.gender === "PREFER_NO_ANSWER"
  ) {
    return "they/them/theirs";
  }
  return "-";
};

export const formatPayoutStatus = (status: PayoutStatus) => {
  switch (status) {
    case "PAID":
      return "Paid";
    case "DUE":
      return "Due";
  }
};

export const formatAttorneyPayoutAmmount = (appointment: Appointment) => {
  if (
    appointment.purchaseItem.payoutStatus === "DUE" &&
    appointment.purchaseItem.pricePaidInCents &&
    appointment.purchaseItem.feePaidInCents
  ) {
    const expectedPayout =
      appointment.purchaseItem.pricePaidInCents -
      appointment.purchaseItem.feePaidInCents;
    return formatCurrency(expectedPayout, "USD");
  } else if (
    appointment.purchaseItem.payoutStatus === "PAID" &&
    appointment.purchaseItem.pricePaidToAttorneyInCents
  ) {
    return formatCurrency(
      appointment.purchaseItem.pricePaidToAttorneyInCents,
      "USD",
    );
  }
  return "-";
};

export const formatAttorneyServiceDetails = (
  serviceType: ServiceType,
  stateName?: string,
) => {
  const representationDetails = [
    "Phone call with attorney to discuss and review your agreement",
    "Dedicated legal advice from attorney",
    "Full explanation of your rights & options",
    "Customization of your agreement",
    "Attorney signature ",
    "E-signature & online notarization for you",
    "Flat fee, only $699—no retainer required",
  ];

  const qADetails = [
    "Receive legal advice on your specific legal concerns",
    `20 minute phone call with a licensed attorney in ${stateName ?? "your state"}.`,
  ];

  switch (serviceType) {
    case "Q_A":
      return qADetails;
    case "REPRESENTATION":
      return representationDetails;
  }
};

export const formatAttorneyServiceNameAndDescription = (
  serviceType: ServiceType,
  stateName: string,
  user: User,
  partner: User | null,
  hasDetails: boolean,
  representationPayment?: PurchaseItem,
) => {
  switch (serviceType) {
    case "Q_A":
      return {
        name: "Attorney Q&A Session",
        description:
          "Connect with experienced attorneys from the nation's top universities. Our legal experts have years of experience and are here to provide clear, reliable guidance to help you navigate your concerns with confidence.",
      };
    case "REPRESENTATION":
      if (
        partner &&
        !hasDetails &&
        representationPayment &&
        representationPayment.createdById === partner.id
      ) {
        return {
          name: "Attorney Representation",
          description: `It's time for your attorney journey, ${formatUserFirstName(user)}! ${partner.firstName ?? "Your partner"} has already purchased legal representation for you. Now, choose which ${stateName} attorney you'd like to work with to review, advise, and e-sign your agreement on your behalf.`,
        };
      }
      return {
        name: "Attorney Representation",
        description: `It's time for your attorney journey, ${formatUserFirstName(user)}! Choose which ${stateName} attorney you'd like to work with to review, advise, and e-sign your agreement on your behalf.`,
      };
  }
};

export const formatAttorneyBookingLink = (bookingLink: string, user: User) => {
  const name = encodeURIComponent(formatUserFullName(user, "firstLast"));
  const email = encodeURIComponent(user.email);
  const linkWithParams = `${bookingLink}?name=${name}&email=${email}`;
  return linkWithParams;
};

export const formatAttorneyStage = (stage: AttorneyStage) => {
  switch (stage) {
    case "FORM_MISSING":
      return {
        label: "Form Missing",
        color: COLOR_PURPLE_TEXT,
      };
    case "UNDER_REVIEW":
      return {
        label: "Under Review",
        color: COLOR_UNDER_REVIEW_TEXT,
      };
    case "APPROVED":
      return {
        label: "Approved",
        color: COLOR_PAID_TEXT,
      };
    case "DECLINED":
      return {
        label: "Declined",
        color: COLOR_DUE_TEXT,
      };
    case "DEACTIVATED":
      return {
        label: "Deactivated",
        color: COLOR_DEACTIVATED_TEXT,
      };
  }
};

export const formatLawFirmStatus = (status: boolean) => {
  switch (status) {
    case true:
      return {
        label: "Active",
        color: COLOR_PAID_TEXT,
        backgroundColor: COLOR_GREEN_BACKGROUND,
      };
    case false:
      return {
        label: "Inactive",
        color: COLOR_DEACTIVATED_TEXT,
        backgroundColor: COLOR_ERROR_BACKGROUND,
      };
  }
};

export const formatLawFirmAttorneyCount = (attorneyCount: number) => {
  return attorneyCount === 1 ? "1 attorney" : `${attorneyCount} attorneys`;
};

export type DocumentForFormatting = Pick<
  DocumentWithRelations,
  "userA" | "userB" | "template" | "stateId"
>;

const getTermForDocument = (document: DocumentForFormatting) => {
  const templateState = document.template.documentTemplateStates.find(
    (templateState) => templateState.stateId === document.stateId,
  );
  return templateState?.termForDocument ?? "Prenuptial Agreement";
};

export const formatDocumentPdfFilename = (document: DocumentForFormatting) => {
  const userAName = formatUserFullName(document.userA, "full");
  const userBName = formatUserFullName(document.userB, "full");
  return `${userAName}_${userBName}_${getTermForDocument(document)}.pdf`;
};

export const formatPendingRepresentationBodyText = (
  document: DocumentWithRelations,
  currentUser: User,
) => {
  const partner =
    currentUser.id === document.userA.id ? document.userB : document.userA;
  const partnerHasAttorney = Boolean(
    partner?.id === document.userAId
      ? document.attorneyAId
      : document.attorneyBId,
  );

  if (!partner || !partnerHasAttorney) {
    return `You've completed all steps in generating your prenup and it's time to meet with your attorney to finalize your prenup.`;
  }

  return `You've completed all steps in generating your prenup and it's time to meet with your attorney to finalize your prenup. Your partner, ${partner.firstName}, has scheduled time to meet with theirs.`;
};

export const formatAttorneyReviewStatus = (status: AttorneyReviewStatus) => {
  switch (status) {
    case "PENDING":
      return {
        label: "Pending",
        color: COLOR_DUE_TEXT,
      };
    case "APPROVED":
      return {
        label: "Approved",
        color: COLOR_PAID_TEXT,
      };
    case "DISABLED":
      return {
        label: "Disabled",
        color: COLOR_DEACTIVATED_TEXT,
      };
  }
};

/**
 * Capitalizes the first letter of a name field.
 * Preserves the rest of the string as-is to avoid breaking intentional formatting like "McDonald" or "von Trapp".
 */
export const capitalizeFirstLetter = (value: string): string => {
  if (!value || value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};
